import { useToast } from '@chakra-ui/react'
import type { AxiosError } from 'axios'
import type { DetailedUser, EditUser, UserProperties } from 'BrokerTsLib/src/dtos'
import type {
  ModelState
} from 'brokertslib/src/dtos/Shared/ModelState'
import {
  BadRequestError
} from 'brokertslib/src/dtos/Shared/ModelState'
import {
  useCreateUser,
  useGetOrganizationUnitById
} from 'BrokerTsLib/src/hooks/useOrganizationUnit'
import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import { DashboardHeader } from '@/containers/dashboard/DashboardHeader'
import { UserForm } from '@/containers/user/UserForm'
import { SidebarProvider } from '@/context/SidebarContext'
import { PageWrapper } from '@/layouts/components/PageWrapper'
import { Sidebar } from '@/layouts/components/Sidebar'
import type { BreadcrumbItems } from 'BrokerTsLib/src/containers/Breadcrumbs'
import { Breadcrumbs } from 'BrokerTsLib/src/containers/Breadcrumbs'

export const OrganizationCreateUserPage = () => {
  const { organizationUnitId: organizationUnitIdParam } = useParams()
  const navigate = useNavigate()
  const toast = useToast()
  const organizationUnitId = Number(organizationUnitIdParam)

  const { data: organizationData } = useGetOrganizationUnitById(organizationUnitId)
  const organization = organizationData?.data

  const [user, setUser] = useState<DetailedUser | null>(null)
  const [properties] = useState({} as UserProperties)

  useEffect(() => {
    if (!organizationData) return

    setUser({
      insuringAllowed: true,
      valid: true,
      organizationUnitId
    } as EditUser)
  }, [organizationData])

  const { mutateAsync: create } = useCreateUser()

  const onSubmit = (values: EditUser) =>
    create(values)
      .then(() => {
        navigate('/organization/' + organizationUnitId)
      })
      .catch((e: AxiosError) => {
        if (e.response?.status === 400)
          return new BadRequestError(e.response?.data as ModelState<EditUser>)

        toast({
          title: 'Chyba při vytváření uživatele',
          description: `${e.response?.status}: ${e.response?.statusText}`,
          status: 'error',
        })

        return e.cause ?? new Error('Unknown error')
      })

  const breadcrumbs = useMemo(() => {
    const items: BreadcrumbItems = [
      { to: '/organization/list', label: 'Seznam organizací' },
    ]

    if (organization?.superiorOrganizationUnitId)
      items.push({
        to: `/organization/${organization?.superiorOrganizationUnitId}`,
        label:
          organization?.superiorOrganizationUnitName ?? 'Nadřízená organizace',
      })

    items.push({
      to: `/organization/${organizationUnitId}`,
      label: organization?.name ?? 'Organizace',
    })

    items.push({
      to: `/organization/${organizationUnitId}/users/create`,
      label: 'Vytvořit uživatele',
      current: true,
    })

    return items
  }, [organization])

  return (
    <SidebarProvider>
      <PageWrapper
        header={
          <>
            <Breadcrumbs items={breadcrumbs} />
            <DashboardHeader
              sx={{ py: 16 }}
              title="Vytvořit nového uživatele"
            />
          </>
        }
        sidebar={<Sidebar />}
        mainProps={{
          sx: {
            pt: { base: 0, md: 8 },
            gap: 6,
            display: 'grid',
            alignContent: 'baseline',
          },
        }}
      >
        {user && properties && (
          <UserForm
            user={user}
            properties={properties}
            onSubmit={onSubmit}
            isCreate
          />
        )}
      </PageWrapper>
    </SidebarProvider>
  )
}
